import { Heading } from '@/components';
import { asText } from '@prismicio/helpers';
import { PrismicLink, PrismicRichText } from '@prismicio/react';

const EMBEDDED_CONTENT_WIDTH = '100%';
const EMBEDDED_CONTENT_HEIGHT = '500';
const PLACEHOLDER_ALT = /^image$/i;

/**
 * Rich text images must always expose an `alt` attribute (N-05).
 * Meaningful CMS alt is preferred; empty/placeholder alts are treated as decorative
 * so we never omit `alt` or fall back to the word "Image" (N-01).
 */
const resolveRichTextImageAlt = alt => {
  const trimmed = typeof alt === 'string' ? alt.trim() : '';
  if (!trimmed || PLACEHOLDER_ALT.test(trimmed)) {
    return { alt: '', decorative: true };
  }

  return { alt: trimmed, decorative: false };
};

/** Mirrors Prismic's default image markup (`.block-img` + optional link). */
const richTextImage = ({ node, key }) => {
  const { alt, decorative } = resolveRichTextImageAlt(node.alt);

  const img = (
    <img
      alt={alt}
      aria-hidden={decorative || undefined}
      data-copyright={node.copyright || undefined}
      src={node.url}
    />
  );

  return (
    <p className="block-img" key={key}>
      {node.linkTo ? <PrismicLink field={node.linkTo}>{img}</PrismicLink> : img}
    </p>
  );
};

const htmlSerializer = {
  image: richTextImage,
  embed: ({ node }) => {
    const finalHtml = node.oembed.html;

    // Parse the finalHtml to extract the iframe tag
    const iframeRegex = /<iframe[^>]*>/;
    const iframeTag = finalHtml.match(iframeRegex)[0];

    // Parse the iframe tag to extract existing attributes
    const widthRegex = /width="([^"]*)"/;
    const heightRegex = /height="([^"]*)"/;

    // Replace existing width and height attributes with new values
    const updatedIframeTag = iframeTag
      .replace(widthRegex, `width="${EMBEDDED_CONTENT_WIDTH}"`)
      .replace(heightRegex, `height="${EMBEDDED_CONTENT_HEIGHT}"`);

    // Replace the old iframe tag with the updated one in the finalHtml
    const updatedFinalHtml = finalHtml.replace(iframeRegex, updatedIframeTag);

    return (
      <div
        dangerouslySetInnerHTML={{ __html: updatedFinalHtml }}
        data-oembed={node.oembed.embed_url}
        data-oembed-provider={node.oembed.provider_name}
        data-oembed-type={node.oembed.type}
      />
    );
  },
  heading1: ({ children }) => <Heading level={1}>{children}</Heading>,
  heading2: ({ children }) => <Heading level={2}>{children}</Heading>,
  heading3: ({ children }) => <Heading level={3}>{children}</Heading>,
  heading4: ({ children }) => <Heading level={4}>{children}</Heading>,
  paragraph: ({ children }) => <p>{children}</p>,
};

const htmlSerializerWithProps = (style = {}, className) => ({
  image: richTextImage,
  heading1: ({ children }) => (
    <Heading className={className} level={1} style={style}>
      {children}
    </Heading>
  ),
  heading2: ({ children }) => (
    <Heading className={className} level={2} style={style}>
      {children}
    </Heading>
  ),
  heading3: ({ children }) => (
    <Heading className={className} level={3} style={style}>
      {children}
    </Heading>
  ),
  heading4: ({ children }) => (
    <Heading className={className} level={4} style={style}>
      {children}
    </Heading>
  ),
  heading5: ({ children }) => (
    <Heading className={className} level={5} style={style}>
      {children}
    </Heading>
  ),
  heading6: ({ children }) => (
    <Heading className={className} level={6} style={style}>
      {children}
    </Heading>
  ),
  listItem: ({ children }) => (
    <li className={className} style={style}>
      {children}
    </li>
  ),
  oListItem: ({ children }) => (
    <li className={className} style={style}>
      {children}
    </li>
  ),
  paragraph: ({ children }) => (
    <p className={className} style={style}>
      {children}
    </p>
  ),
});

/**
 * @typedef {Object} HeadingAsParagraphProps
 * @property {import('@prismicio/client').RichTextField | null | undefined} [field]
 * @property {string} [className]
 * @property {string} [color]
 * @property {import('react').CSSProperties} [style]
 */

/** Renders heading/paragraph rich text as <p>, preserving line breaks. */
/** @param {HeadingAsParagraphProps} props */
const HeadingAsParagraph = ({
  field,
  className,
  color,
  style: styleProp = {},
}) => {
  if (!asText(field)) {
    return null;
  }

  const style = { ...styleProp, ...(color ? { color } : undefined) };

  const asParagraph = ({ children }) => (
    <p className={className} style={style}>
      {children}
    </p>
  );

  return (
    <PrismicRichText
      components={{
        heading1: asParagraph,
        heading2: asParagraph,
        heading3: asParagraph,
        heading4: asParagraph,
        heading5: asParagraph,
        heading6: asParagraph,
        paragraph: asParagraph,
      }}
      field={field}
    />
  );
};

export { htmlSerializer, htmlSerializerWithProps, HeadingAsParagraph };
